import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { QrCode, ArrowLeft, Mail, Lock, Loader2, UserPlus, LogIn, User, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { signIn, signUp, getIdToken, resetPassword, logOut } from "../lib/firebase";

const API = process.env.REACT_APP_BACKEND_URL;

// Firebase error code to user-friendly message mapping
const getFirebaseErrorMessage = (error) => {
  const errorCode = error.code || '';
  const errorMap = {
    'auth/user-not-found': 'No account found with this email. Please sign up.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/invalid-credential': 'Invalid email or password. Please check and try again.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/email-already-in-use': 'This email is already registered. Please login instead.',
    'auth/weak-password': 'Password is too weak. Use at least 6 characters.',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
    'auth/network-request-failed': 'Network error. Please check your connection.',
    'auth/user-disabled': 'This account has been disabled. Contact support.',
    'auth/operation-not-allowed': 'This login method is not enabled.',
    'auth/requires-recent-login': 'Please log in again to continue.',
  };
  return errorMap[errorCode] || error.message || 'An unexpected error occurred.';
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  
  // Signup form
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");
  const [signupName, setSignupName] = useState("");

  // Forgot password
  const [resetEmail, setResetEmail] = useState("");

  // Password visibility toggles
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  /**
   * Handle Login Flow:
   * 1. Authenticate with Firebase using signInWithEmailAndPassword
   * 2. Get Firebase ID token
   * 3. Send token to backend /api/auth/me
   * 4. Backend verifies token and auto-creates user if needed
   * 5. Navigate to dashboard on success
   */
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginEmail.trim()) {
      toast.error("Please enter your email");
      return;
    }
    
    if (!loginPassword) {
      toast.error("Please enter your password");
      return;
    }

    setLoading(true);
    try {
      // Step 1: Authenticate with Firebase
      console.log("[Auth] Attempting Firebase login...");
      await signIn(loginEmail.trim(), loginPassword);
      console.log("[Auth] Firebase login successful");
      
      // Step 2: Get Firebase ID token
      const token = await getIdToken();
      if (!token) {
        throw new Error("Failed to get authentication token");
      }
      console.log("[Auth] Got Firebase ID token");
      
      // Step 3: Verify with backend and sync user
      const response = await fetch(`${API}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401) {
          throw new Error("Session expired. Please login again.");
        } else if (response.status === 403) {
          throw new Error("Your account has been blocked. Contact support.");
        }
        throw new Error(errorData.detail || "Failed to verify account");
      }
      
      const userData = await response.json();
      console.log("[Auth] Backend sync successful", { autoCreated: userData.auto_created });
      
      // Check if user is admin - block login
      if (userData.role === 'admin') {
        await logOut(); // Sign out the admin
        toast.error("No admin login is allowed from here");
        return;
      }
      
      // Step 4: Success - navigate to dashboard
      if (userData.auto_created) {
        toast.success("Welcome! Your account has been set up.");
      } else {
        toast.success("Login successful!");
      }
      
      navigate("/dashboard", { replace: true });
      
    } catch (error) {
      console.error("[Auth] Login error:", error);
      
      // Handle Firebase-specific errors
      if (error.code) {
        const message = getFirebaseErrorMessage(error);
        toast.error(message);
        
        // Switch to signup tab if user not found
        if (error.code === 'auth/user-not-found') {
          setActiveTab("signup");
          setSignupEmail(loginEmail);
        }
        // Show forgot password option for wrong password
        else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
          setShowForgotPassword(true);
        }
      } else {
        // Handle backend or network errors
        toast.error(error.message || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle Signup Flow:
   * 1. Create user in Firebase using createUserWithEmailAndPassword
   * 2. If email exists, try to sign in instead
   * 3. Get Firebase ID token
   * 4. Register in backend /api/auth/register
   * 5. Navigate to dashboard on success
   */
  const handleSignup = async (e) => {
    e.preventDefault();
    
    // Validate inputs
    if (!signupName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    
    if (!signupEmail.trim()) {
      toast.error("Please enter your email");
      return;
    }
    
    if (!signupPassword) {
      toast.error("Please enter a password");
      return;
    }
    
    if (signupPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (signupPassword !== signupConfirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      let isExistingFirebaseUser = false;
      
      // Step 1: Try to create Firebase user
      try {
        console.log("[Auth] Attempting Firebase signup...");
        await signUp(signupEmail.trim(), signupPassword);
        console.log("[Auth] Firebase signup successful");
      } catch (firebaseError) {
        console.log("[Auth] Firebase signup error:", firebaseError.code);
        
        // If email already exists in Firebase, try to sign in
        if (firebaseError.code === 'auth/email-already-in-use') {
          console.log("[Auth] Email exists in Firebase, attempting login...");
          isExistingFirebaseUser = true;
          
          try {
            await signIn(signupEmail.trim(), signupPassword);
            console.log("[Auth] Signed in existing Firebase user");
          } catch (signInError) {
            // Wrong password for existing account
            if (signInError.code === 'auth/wrong-password' || signInError.code === 'auth/invalid-credential') {
              toast.error("This email is already registered with a different password. Please login instead.");
              setActiveTab("login");
              setLoginEmail(signupEmail);
              setShowForgotPassword(true);
              return;
            }
            throw signInError;
          }
        } else {
          // Other Firebase errors
          throw firebaseError;
        }
      }
      
      // Step 2: Get Firebase ID token
      const token = await getIdToken();
      if (!token) {
        throw new Error("Failed to get authentication token");
      }
      console.log("[Auth] Got Firebase ID token");
      
      // Step 3: Register/sync with backend
      const response = await fetch(`${API}/api/auth/register`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: signupEmail.trim(),
          name: signupName.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // "Already registered" is fine - user exists in both Firebase and MongoDB
        if (!errorData.detail?.includes("already registered")) {
          throw new Error(errorData.detail || "Registration failed");
        }
        console.log("[Auth] User already registered in backend, proceeding...");
      } else {
        console.log("[Auth] Backend registration successful");
      }
      
      // Step 4: Success
      if (isExistingFirebaseUser) {
        toast.success("Welcome back! Signed in successfully.");
      } else {
        toast.success("Account created successfully!");
      }
      
      navigate("/dashboard", { replace: true });
      
    } catch (error) {
      console.error("[Auth] Signup error:", error);
      
      if (error.code) {
        toast.error(getFirebaseErrorMessage(error));
      } else {
        toast.error(error.message || "Signup failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle Forgot Password
   */
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    const email = resetEmail.trim() || loginEmail.trim();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      await resetPassword(email);
      toast.success("Password reset email sent! Check your inbox.");
      setShowForgotPassword(false);
    } catch (error) {
      console.error("[Auth] Password reset error:", error);
      if (error.code === 'auth/user-not-found') {
        toast.error("No account found with this email");
      } else {
        toast.error(getFirebaseErrorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-6 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/")}
          data-testid="back-btn"
          className="hover:bg-gray-200"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          <QrCode className="w-8 h-8 text-[#2563EB]" />
          <span className="text-xl font-bold text-[#1F2933]" style={{ fontFamily: 'Manrope' }}>
            QuickQRID
          </span>
        </div>
      </div>

      {/* Auth Card */}
      <Card className="flex-1 bg-white shadow-sm max-w-md mx-auto w-full">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-2xl font-bold text-[#1F2933]" style={{ fontFamily: 'Manrope' }}>
            Welcome
          </CardTitle>
          <CardDescription className="text-[#6B7280]">
            Sign in or create an account to manage your QR codes
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" className="flex items-center gap-2" data-testid="login-tab">
                <LogIn className="w-4 h-4" />
                Login
              </TabsTrigger>
              <TabsTrigger value="signup" className="flex items-center gap-2" data-testid="signup-tab">
                <UserPlus className="w-4 h-4" />
                Sign Up
              </TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="Enter your email"
                      className="pl-10"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      disabled={loading}
                      data-testid="login-email-input"
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="login-password"
                      type={showLoginPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      className="pl-10 pr-10"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      disabled={loading}
                      data-testid="login-password-input"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="login-password-toggle"
                    >
                      {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {showForgotPassword && (
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => {
                        setResetEmail(loginEmail);
                        handleForgotPassword({ preventDefault: () => {} });
                      }}
                      className="text-sm text-[#2563EB] hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] h-12"
                  disabled={loading}
                  data-testid="login-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <LogIn className="w-5 h-5 mr-2" />
                      Sign In
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>

            {/* Signup Tab */}
            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="Enter your name"
                      className="pl-10"
                      value={signupName}
                      onChange={(e) => setSignupName(e.target.value)}
                      disabled={loading}
                      data-testid="signup-name-input"
                      autoComplete="name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="Enter your email"
                      className="pl-10"
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      disabled={loading}
                      data-testid="signup-email-input"
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="signup-password"
                      type={showSignupPassword ? "text" : "password"}
                      placeholder="Create a password (min 6 chars)"
                      className="pl-10 pr-10"
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      disabled={loading}
                      data-testid="signup-password-input"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignupPassword(!showSignupPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="signup-password-toggle"
                    >
                      {showSignupPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-confirm">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="signup-confirm"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      className="pl-10 pr-10"
                      value={signupConfirmPassword}
                      onChange={(e) => setSignupConfirmPassword(e.target.value)}
                      disabled={loading}
                      data-testid="signup-confirm-input"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="signup-confirm-toggle"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] h-12"
                  disabled={loading}
                  data-testid="signup-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <UserPlus className="w-5 h-5 mr-2" />
                      Create Account
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Version */}
      <p className="text-center text-gray-400 text-xs mt-4" data-testid="login-version">
        v{window.APP_VERSION || '2.0.0'}
      </p>
    </div>
  );
};

export default LoginPage;
